/**
 * Loop Share Utility
 *
 * Encode/decode a saved loop as a self-contained URL hash payload.
 * No backend required — the full loop descriptor is base64-encoded in the URL.
 *
 * URL format: https://looperc.app/#loop=<base64(JSON)>
 *
 * Ticket 20260408b
 */

import type { SavedLoop } from '../generators/types';
import { STYLE_GROUPS } from '../config/app.config';
import { SECTION_RANGES } from '../generators/songPatternGenerator';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Minimal loop descriptor embedded in the shareable URL.
 * v=1 is the schema version — increment if the shape changes.
 *
 * Includes per-segment durations, token types, and song structure slots
 * so both the song pattern overview and sequence strip render correctly
 * for the recipient without any extra Qdrant queries.
 */
export interface SharePayload {
    v: 1;
    album: string;
    tokens: string[];
    bpm: number;
    genre: string;
    style_group: string;
    bar_size: 'SHORT' | 'MID' | 'LARGE' | 'CUSTOM';
    /** Per-segment durations in seconds (parallel to tokens[]) */
    durations: number[];
    /** Per-segment token types (parallel to tokens[]) */
    token_types: string[];
    /** Song structure slots — one entry per section (verse, chorus, etc.) */
    slots: Array<{ token: string; duration: number }>;
    /** Custom pattern — only present when bar_size === 'CUSTOM' */
    custom_pattern?: Array<Record<string, number>>;
    /** Time signature numerator: 4 = 4/4, 3 = 3/4 or 6/8. Defaults to 4.
     *  Optional for backwards compatibility with URLs encoded before this field existed. */
    pulse_compas?: number;
}

// ---------------------------------------------------------------------------
// Encode
// ---------------------------------------------------------------------------

/**
 * Build a shareable URL from a saved loop.
 * Returns the full URL with the base64-encoded payload in the hash.
 */
export function encodeLoopToUrl(loop: SavedLoop): string {
    const payload: SharePayload = {
        v: 1,
        album: loop.album,
        tokens: loop.tokens,
        bpm: loop.bpm,
        genre: loop.genre,
        style_group: loop.style_group,
        bar_size: loop.bar_size,
        durations: loop.durations,
        token_types: loop.token_types,
        slots: loop.slots,
        custom_pattern: loop.custom_pattern,
        pulse_compas: loop.pulse_compas ?? 4,
    };
    const b64 = btoa(JSON.stringify(payload));
    return `${window.location.origin}/#loop=${b64}`;
}

// ---------------------------------------------------------------------------
// Decode
// ---------------------------------------------------------------------------

// Validation limits. Ticket 20260923b.
const MAX_STRING_LENGTH = 200;
/** Real loops stay well below this; LARGE/CUSTOM loops of short samples can reach a few hundred. */
const MAX_TOKENS = 500;
const MAX_PATTERN_SECTIONS = 100;
const MIN_BPM = 30;
const MAX_BPM = 300;
const MIN_SECTION_BARS = 0.25;
const MAX_SECTION_BARS = 32;
const BAR_SIZES: ReadonlySet<string> = new Set(['SHORT', 'MID', 'LARGE', 'CUSTOM']);
const SECTION_NAMES: ReadonlySet<string> = new Set(Object.keys(SECTION_RANGES));
/** Genres are plain words (funk, hiphop, ...); the genre is stored when a shared loop is saved. */
const GENRE_PATTERN = /^[A-Za-z0-9 _-]{1,50}$/;

const isShortString = (x: unknown): x is string =>
    typeof x === 'string' && x.length > 0 && x.length <= MAX_STRING_LENGTH;
/** Token types may be '' (loops re-saved from a link without token_types). */
const isTokenType = (x: unknown): x is string =>
    typeof x === 'string' && x.length <= MAX_STRING_LENGTH;
const isFiniteNumber = (x: unknown): x is number =>
    typeof x === 'number' && Number.isFinite(x);
const isArrayOf = <T>(x: unknown, pred: (v: unknown) => v is T, maxLen: number): x is T[] =>
    Array.isArray(x) && x.length <= maxLen && x.every(pred);

function isSlot(x: unknown): x is { token: string; duration: number } {
    if (typeof x !== 'object' || x === null) return false;
    const s = x as Record<string, unknown>;
    return isShortString(s.token) && isFiniteNumber(s.duration);
}

function isPatternSection(x: unknown): x is Record<string, number> {
    if (typeof x !== 'object' || x === null || Array.isArray(x)) return false;
    const entries = Object.entries(x);
    if (entries.length !== 1) return false;
    const [section, bars] = entries[0];
    return SECTION_NAMES.has(section) && isFiniteNumber(bars)
        && bars >= MIN_SECTION_BARS && bars <= MAX_SECTION_BARS;
}

/**
 * Validate an untrusted, already-parsed share payload.
 * Returns a new object holding only the known fields, or null if any field is
 * missing, has the wrong type, or is outside the values the app produces.
 * Share links are attacker-controllable, so nothing is trusted as-is.
 */
export function validateSharePayload(parsed: unknown): SharePayload | null {
    if (typeof parsed !== 'object' || parsed === null) return null;
    const p = parsed as Record<string, unknown>;

    if (p.v !== 1) return null;
    if (!isShortString(p.album)) return null;
    if (typeof p.genre !== 'string' || !GENRE_PATTERN.test(p.genre)) return null;
    if (typeof p.style_group !== 'string' || !Object.prototype.hasOwnProperty.call(STYLE_GROUPS, p.style_group)) return null;
    if (typeof p.bar_size !== 'string' || !BAR_SIZES.has(p.bar_size)) return null;
    if (!isFiniteNumber(p.bpm) || p.bpm < MIN_BPM || p.bpm > MAX_BPM) return null;
    if (!isArrayOf(p.tokens, isShortString, MAX_TOKENS) || p.tokens.length === 0) return null;
    const n = p.tokens.length;

    // Optional or empty in old links (the Worker returns [] for loops saved before
    // token_types existed); otherwise they must line up with tokens
    const alignedOrEmpty = (x: unknown[]) => x.length === 0 || x.length === n;
    if (p.durations !== undefined && !(isArrayOf(p.durations, isFiniteNumber, n) && alignedOrEmpty(p.durations))) return null;
    if (p.token_types !== undefined && !(isArrayOf(p.token_types, isTokenType, n) && alignedOrEmpty(p.token_types))) return null;
    if (p.slots !== undefined && !isArrayOf(p.slots, isSlot, MAX_TOKENS)) return null;
    if (p.custom_pattern !== undefined && p.custom_pattern !== null
        && !isArrayOf(p.custom_pattern, isPatternSection, MAX_PATTERN_SECTIONS)) return null;
    const pulseCompas = p.pulse_compas ?? 4;
    if (pulseCompas !== 3 && pulseCompas !== 4) return null;

    return {
        v: 1,
        album: p.album,
        tokens: p.tokens,
        bpm: p.bpm,
        genre: p.genre,
        style_group: p.style_group,
        bar_size: p.bar_size as SharePayload['bar_size'],
        durations: (p.durations as number[] | undefined) ?? [],
        token_types: (p.token_types as string[] | undefined) ?? [],
        slots: (p.slots as SharePayload['slots'] | undefined) ?? [],
        custom_pattern: (p.custom_pattern as SharePayload['custom_pattern'] | null) ?? undefined,
        pulse_compas: pulseCompas,
    };
}

/** True when the URL carries a share hash, valid or not. */
export function hasShareLinkInUrl(): boolean {
    return window.location.hash.startsWith('#loop=');
}

/**
 * Parse the current URL hash and return the SharePayload if present.
 * Returns null if the hash is absent, malformed, or fails validateSharePayload().
 */
export function decodeLoopFromUrl(): SharePayload | null {
    const match = window.location.hash.match(/#loop=([A-Za-z0-9+/=]+)/);
    if (!match) return null;
    try {
        return validateSharePayload(JSON.parse(atob(match[1])));
    } catch {
        return null;
    }
}
